import { forwardRef, useMemo, useState } from 'react'
import {
  Checkbox,
  CloseButton,
  Group,
  MultiSelect as MSelect,
  MultiSelectProps,
  MultiSelectValueProps,
  SelectItem,
  Text,
} from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { TextLabelInputTradeView } from './TextLabelInputTradeView'

const TRUNCATE_AFTER = 2

/**
 * Font size for both the selected-value chips and the inner search input.
 *
 * The chips are rendered at an absolute 14px by makeValueComponent, but Mantine
 * sizes the search input from `theme.fontSizes[size]`, which is rem-based. This app
 * sets `html { font-size: 10px !important }` (general.css), so the theme's rem(14)
 * resolves to 8.75px — free typing came out noticeably smaller than the chips
 * sitting right beside it. Pinning the same absolute value keeps them consistent.
 */
const VALUE_FONT_SIZE = '14px'

type Props = MultiSelectProps & {
  wrapperType?: 'default' | 'trade'
  /**
   * Float already-selected options to the top of the dropdown. For lists of a few
   * hundred entities or deals, the alternative is scrolling the whole list to find
   * what you picked. Off by default: on a short list with a meaningful order (a
   * status lifecycle, Buy/Sell) reordering loses more than it gains.
   */
  pinSelectedToTop?: boolean
}

type CheckboxItemProps = {
  label: string
  value: string
  selected?: boolean
}

const CheckboxItem = forwardRef<HTMLDivElement, CheckboxItemProps>(
  ({ label, selected, ...rest }, ref) => (
    <div ref={ref} {...rest}>
      <Group noWrap spacing='sm'>
        <Checkbox checked={!!selected} readOnly tabIndex={-1} size='sm' />
        <Text fz='14px'>{label}</Text>
      </Group>
    </div>
  ),
)

CheckboxItem.displayName = 'CheckboxItem'

/**
 * Wraps a chip's remove handler: swallow the event, then run the handler WITH that
 * event.
 *
 * Forwarding it is load-bearing. Mantine's own onRemove implementation is
 * `(event) => { event.preventDefault(); event.stopPropagation(); handleValueRemove(...) }`,
 * so calling it bare threw "Cannot read properties of undefined (reading
 * 'preventDefault')" and the chip could not be removed. TypeScript cannot catch that:
 * MultiSelectValueProps declares `onRemove(): void`, with no parameter, which
 * contradicts the runtime. Hence the test in MultiSelect.spec.ts.
 *
 * Exported for tests.
 */
export const stopAndRun =
  (fn?: (event: React.MouseEvent) => void) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    fn?.(e)
  }

const makeValueComponent = (selectedLabels: string[], onRemoveOverflow: () => void) => {
  const Component = forwardRef<HTMLDivElement, MultiSelectValueProps>(
    ({ label, onRemove, ...others }, ref) => {
      const index = selectedLabels.indexOf(label)
      const total = selectedLabels.length
      const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        lineHeight: 1,
        whiteSpace: 'nowrap' as const,
      }
      if (index < TRUNCATE_AFTER) {
        const isLastVisible = index === Math.min(TRUNCATE_AFTER, total) - 1
        const hasOverflow = total > TRUNCATE_AFTER
        const trailing = !isLastVisible || hasOverflow ? ', ' : ''
        return (
          <div ref={ref} {...others} style={chipStyle}>
            <Text component='span' fz={VALUE_FONT_SIZE} c='#476db9'>{label}</Text>
            <CloseButton
              size={14}
              iconSize={10}
              variant='transparent'
              color='gray'
              onMouseDown={stopAndRun(onRemove)}
              aria-label={`Remove ${label}`}
            />
            {trailing && <Text component='span' fz={VALUE_FONT_SIZE} c='#476db9'>{trailing}</Text>}
          </div>
        )
      }
      if (index === TRUNCATE_AFTER) return (
        <div ref={ref} {...others} style={chipStyle}>
          <Text component='span' fz={VALUE_FONT_SIZE} fw={600} c='#476db9'>+{total - TRUNCATE_AFTER}</Text>
          <CloseButton
            size={14}
            iconSize={10}
            variant='transparent'
            color='gray'
            onMouseDown={stopAndRun(onRemoveOverflow)}
            aria-label='Remove additional selections'
          />
        </div>
      )
      return null
    },
  )
  Component.displayName = 'CommaTruncatedValue'
  return Component
}

function itemLabel(item: string | SelectItem): string {
  if (typeof item === 'string') return item
  return item.label ?? item.value
}

function itemValue(item: string | SelectItem): string {
  if (typeof item === 'string') return item
  return item.value
}

/**
 * Partitions `data` into selected options followed by the rest, keeping the
 * original relative order inside each group so the unselected tail stays
 * alphabetical (or whatever order the caller supplied).
 *
 * Exported for tests.
 */
export function orderSelectedFirst(
  data: ReadonlyArray<string | SelectItem>,
  selectedValues: ReadonlyArray<string>,
): Array<string | SelectItem> {
  if (selectedValues.length === 0) return [...data]
  const selectedSet = new Set(selectedValues)
  const picked: Array<string | SelectItem> = []
  const rest: Array<string | SelectItem> = []
  for (const item of data) {
    if (selectedSet.has(itemValue(item))) picked.push(item)
    else rest.push(item)
  }
  return [...picked, ...rest]
}

export default function MultiSelect({ wrapperType = 'default', pinSelectedToTop = false, value, label, data, onChange, onDropdownOpen, ...props }: Props) {
  const selected = ((value as string[] | undefined) ?? [])
  const labelByValue = new Map<string, string>()
  for (const item of data) {
    labelByValue.set(itemValue(item), itemLabel(item))
  }
  const selectedLabels = selected.map((v) => labelByValue.get(v) ?? v)
  const handleRemoveOverflow = () => onChange?.(selected.slice(0, TRUNCATE_AFTER))
  const selectionKey = selectedLabels.join('|')
  const ValueComponent = useMemo(() => makeValueComponent(selectedLabels, handleRemoveOverflow), [selectionKey])

  // What was selected when the dropdown last opened. The ordering is frozen to
  // this snapshot rather than tracking `selected` live: reordering on every tick
  // would make the item just clicked jump to the top and slide the next row
  // under the cursor, so a second click lands on the wrong option. Seeded from
  // the initial value so the first open is already ordered.
  const [pinnedValues, setPinnedValues] = useState<string[]>(() => (value as string[] | undefined) ?? [])

  const orderedData = useMemo(
    () => (pinSelectedToTop ? orderSelectedFirst(data, pinnedValues) : data),
    [pinSelectedToTop, data, pinnedValues],
  )

  const handleDropdownOpen = () => {
    if (pinSelectedToTop) setPinnedValues(selected)
    onDropdownOpen?.()
  }

  if (wrapperType === 'trade') return (
    <Group noWrap className='dataEntrySubSection'>
      <TextLabelInputTradeView label={label as string}>
        <MSelect
          {...props}
          data={orderedData}
          value={value}
          onChange={onChange}
          onDropdownOpen={handleDropdownOpen}
          itemComponent={CheckboxItem}
          valueComponent={ValueComponent}
          disableSelectedItemFiltering
          classNames={{
            input: 'newInput',
            wrapper: 'newInputWrapper',
            rightSection: 'customRightSection',
          }}
          styles={(theme) => ({
            values: { flexWrap: 'nowrap', overflow: 'hidden' },
            searchInput: { fontSize: VALUE_FONT_SIZE, '&::placeholder': { fontSize: VALUE_FONT_SIZE } },
            item: {
              '&[data-selected]': { backgroundColor: 'transparent', color: theme.black },
              '&[data-selected][data-hovered]': { backgroundColor: theme.colors.gray[1] },
            },
          })}
          rightSection={<IonIcon size='large' name='chevron-down-outline' />}
        />
      </TextLabelInputTradeView>
    </Group>
  )
  return (
    <MSelect
      {...props}
      data={orderedData}
      value={value}
      onChange={onChange}
      onDropdownOpen={handleDropdownOpen}
      label={label}
      itemComponent={CheckboxItem}
      valueComponent={ValueComponent}
      disableSelectedItemFiltering
      rightSection={<IonIcon name='chevron-down-outline' />}
      radius='0.7em'
      styles={(theme) => ({
        label: {
          fontFamily: 'Plus Jakarta Sans',
          color: '#111928',
          fontSize: '1em',
        },
        input: {
          backgroundColor: '#F9FAFB',
          minHeight: '3em',
          borderRadius: '0.7em',
          color: '#6B7280',
          borderWidth: '1px',
          borderColor: '#D1D5DB',
          fontSize: '1em',
          fontFamily: 'Plus Jakarta Sans',
        },
        rightSection: {
          marginRight: '10px',
        },
        values: { flexWrap: 'nowrap', overflow: 'hidden' },
        item: {
          '&[data-selected]': { backgroundColor: 'transparent', color: theme.black },
          '&[data-selected][data-hovered]': { backgroundColor: theme.colors.gray[1] },
        },
      })}
    />
  )
}
